import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LuTrendingUp,
  LuArrowRight,
  LuSlidersHorizontal,
  LuExternalLink,
  LuFlaskConical,
  LuClipboardList,
  LuUpload,
} from 'react-icons/lu';
import PortraitScoreBar from '../oracle/PortraitScoreBar';
import { GOLD_NUMERAL_STYLE } from '../oracle/ScoreNumeral';
import CategoryBreakdown from './CategoryBreakdown';
import CategoryDetail from './CategoryDetail';
import CategoryDetailModal from './CategoryDetailModal';
import { trackAffiliate } from '../oracle/VerdictDisplay';
import { vectorGlyph } from '../oracle/VectorGlyphs';
import { VECTOR_DISPLAY_LABELS, isTaxonomyVerdict } from '../../contracts/oracleApi';
import { vaultEntryPath, ROUTES } from '../../contracts/routes';
import { COPY } from '../../contracts/copyMap';
import { formatPercentile } from '../create/format';

// The Vault SCANS focus view — single component shared by /vault and
// /vault/:scan_id. Design-recreation mockup, desktop VAULT surface:
//
//   ┌──────────┬───────────────────────┬──────────────────┐
//   │ FOCUS    │  portrait 440×587     │ FACIAL FEEDBACK  │
//   │ SCAN     │  [SCORE] press-hold   │ panel (vectors)  │
//   │ meta col │  bar lifts -80px      │ + connector SVG  │
//   ├──────────┴───────────────────────┴──────────────────┤
//   │ YOUR PERSONALIZED REGIMEN — 4 product cards          │
//   ├──────────────────────────────────────────────────────┤
//   │ ‹ thumb strip › · dots · n / N SCANS · FILTER EXPORT │
//   └──────────────────────────────────────────────────────┘
//
// Mobile (below lg): thumb strip → gold caps date → SCAN SCORE gradient
// numeral → SHARE SCAN → metrics-over-portrait (mask fade) → regimen list.
//
// Props:
//   entries:        VaultIndexEntry[]   ordered most-recent first
//   focusEntry:     VaultIndexEntry     the highlighted scan (∈ entries)
//   focusDetail:    VaultEntryDetail    full verdict + recommendations for focus
//   onSelect:       (entry) => void     thumbnail click handler
//   onLoadMore:     () => void          pagination for the thumbnail strip
//   hasMore:        bool
//   loadingMore:    bool
//   onImageError:   (entry) => void     for signed-url resign recovery
//   coachingSummary?: string            italic pull-quote (defaults to museum line)

// Backend overall_score is an int 1-100; the design renders X.X / 10.
function formatScoreOutOfTen(score100) {
  if (score100 == null || Number.isNaN(score100)) return '';
  return (score100 / 10).toFixed(1);
}

// Phase 0 verdict shape carries no per-vector descriptive copy
// (writeVerdictSummary is spec'd but not in the frozen contract). Render a
// short museum-text descriptor derived from the real 1-10 score until the
// AI-summary field is added to the verdict contract.
function vectorDescriptor(score) {
  if (score == null) return '';
  if (score >= 9) return 'Exceptional.';
  if (score >= 8) return 'Strong definition.';
  if (score >= 7) return 'Consistently improving.';
  if (score >= 6) return 'Room to refine.';
  if (score >= 5) return 'Soft baseline.';
  return 'Priority for attention.';
}

// One product per recommended vector, capped at 4 cards (design grid).
// Vector kept with each product so affiliate clicks attribute correctly —
// affiliate CTR is the Phase 0 kill-gate metric.
function regimenProducts(recommendations) {
  return (recommendations || [])
    .flatMap((rec) =>
      (rec.products || []).slice(0, 1).map((p) => ({ ...p, vector: rec.vector })),
    )
    .slice(0, 4);
}

// Sum price_range strings only when every one is an exact "$N" / "$N.NN"
// price. Ranges ("$20-30") and missing prices make a sum a fabrication, so
// we omit the total instead. Returns null when not computable.
function regimenTotal(products) {
  if (!products || products.length === 0) return null;
  let total = 0;
  for (const p of products) {
    const m = /^\s*\$(\d+(?:\.\d{1,2})?)\s*$/.exec(p.price_range || '');
    if (!m) return null;
    total += parseFloat(m[1]);
  }
  return `$${Number.isInteger(total) ? total : total.toFixed(2)}`;
}

function shareScan(scanId) {
  const url = `${window.location.origin}${vaultEntryPath(scanId)}`;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      navigator.share({ title: 'MOGGED', url }).catch(() => {});
      return;
    }
    if (
      typeof navigator !== 'undefined'
      && navigator.clipboard
      && typeof navigator.clipboard.writeText === 'function'
    ) {
      navigator.clipboard.writeText(url).catch(() => {});
    }
  } catch {
    // share is best-effort chrome
  }
}

const VaultFocusView = ({
  focusEntry,
  focusDetail,
  onImageError,
}) => {
  const focusId = focusEntry && focusEntry.scan_id;
  const [lifted, setLifted] = useState(false);

  // Drop the bar + reset category selection whenever focus changes.
  useEffect(() => {
    setLifted(false);
    setSelectedCat(null);
    setCatModalOpen(false);
  }, [focusId]);

  const verdict = focusDetail && focusDetail.verdict;
  const isTax = isTaxonomyVerdict(verdict);
  const categoryScores = (verdict && verdict.categories) || [];
  const vectors = (verdict && verdict.vectors) || [];

  // Category master/detail maps + selection (taxonomy scans only).
  const subScores = {};
  const subNotes = {};
  const catScoreMap = {};
  const catSummaryMap = {};
  for (const s of (verdict && verdict.subcategories) || []) {
    subScores[s.key] = s.score;
    subNotes[s.key] = s.note || '';
  }
  for (const c of categoryScores) {
    catScoreMap[c.key] = c.score;
    catSummaryMap[c.key] = c.summary || '';
  }
  const lowestCatKey = [...categoryScores].sort((a, b) => (a.score ?? 999) - (b.score ?? 999))[0];
  const [selectedCat, setSelectedCat] = useState(null);
  const [catModalOpen, setCatModalOpen] = useState(false);
  const activeCat = selectedCat || (lowestCatKey && lowestCatKey.key) || null;
  const openMobileCat = (key) => { setSelectedCat(key); setCatModalOpen(true); };
  const products = useMemo(
    () => regimenProducts(focusDetail && focusDetail.recommendations),
    [focusDetail],
  );
  const focusScore100 = verdict ? verdict.overall_score : focusEntry && focusEntry.overall_score;
  const mostImproved = focusEntry
    && Array.isArray(focusEntry.top_3_improvement_vectors)
    && focusEntry.top_3_improvement_vectors[0];
  const geometry = focusDetail && focusDetail.geometry;
  const percentileLabel = focusDetail
    ? formatPercentile(focusDetail.learned_percentile)
    : null;

  const handleImageError = useCallback(() => {
    if (onImageError && focusEntry) onImageError(focusEntry);
  }, [onImageError, focusEntry]);

  const liftHandlers = {
    onMouseDown: () => setLifted(true),
    onMouseUp: () => setLifted(false),
    onMouseLeave: () => setLifted(false),
    onTouchStart: () => setLifted(true),
    onTouchEnd: () => setLifted(false),
  };

  // Mobile SCAN SCORE + SHARE block. Stacked above the portrait for legacy
  // scans; overlaid onto the portrait's faded-left region for taxonomy scans so
  // it doesn't claim its own vertical space.
  const mobileScoreBlock = (
    <>
      <p
        className="font-display uppercase tracking-[0.2em] text-bone-dim m-0"
        style={{ fontSize: 11 }}
      >
        {COPY.VAULT_SCAN_SCORE_LABEL}
      </p>
      <p
        className="font-display font-medium m-0 mt-1 tabular-nums"
        style={{ fontSize: 84, lineHeight: 1, ...GOLD_NUMERAL_STYLE }}
      >
        {focusScore100 != null ? formatScoreOutOfTen(focusScore100) : '—'}
      </p>
      {percentileLabel && (
        <p className="font-display text-bone-dim m-0 mt-1.5" style={{ fontSize: 13 }}>
          <span className="uppercase tracking-[0.2em] text-aurum" style={{ fontSize: 11 }}>
            {COPY.VERDICT_PERCENTILE_LABEL}
          </span>{' '}
          · {percentileLabel}
        </p>
      )}
      <button
        type="button"
        onClick={() => focusId && shareScan(focusId)}
        className="inline-flex items-center gap-2 mt-3.5 bg-transparent border border-[rgba(255,255,255,0.22)] rounded-thumb px-4 py-3 cursor-pointer font-display uppercase tracking-[0.2em] text-[#E8E2D6] hover:border-[rgba(255,255,255,0.45)] transition-colors"
        style={{ fontSize: 11 }}
      >
        <span>{COPY.VAULT_SHARE_CTA}</span>
        <LuUpload size={13} aria-hidden="true" />
      </button>
    </>
  );

  return (
    <div className="flex flex-col">
      {/* ── DESKTOP focus row ─────────────────────────────────────────── */}
      <motion.div
        key={focusId || 'empty'}
        className="hidden lg:flex gap-[30px] mt-8"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Left info column — legacy scans only (taxonomy is left-aligned). */}
        {!isTax && (
        <div className="w-[248px] shrink-0 pt-6">
          {mostImproved && (
            <span className="inline-flex items-center gap-2 mt-5 px-4 py-2 border border-[rgba(255,255,255,0.22)] rounded-full">
              <span
                className="font-display uppercase tracking-[0.2em] text-[#E8E2D6]"
                style={{ fontSize: 11 }}
              >
                {COPY.VAULT_MOST_IMPROVED_TAG} · {VECTOR_DISPLAY_LABELS[mostImproved] || mostImproved}
              </span>
              <LuTrendingUp size={12} className="text-[#E8E2D6]" aria-hidden="true" />
            </span>
          )}
          {percentileLabel && (
            <div className="mt-5">
              <p
                className="font-display uppercase tracking-[0.22em] text-bone-dim m-0"
                style={{ fontSize: 11 }}
              >
                {COPY.VERDICT_PERCENTILE_LABEL}
              </p>
              <p
                className="font-display font-medium text-aurum m-0 mt-1 tabular-nums"
                style={{ fontSize: 30, lineHeight: 1 }}
              >
                {percentileLabel}
              </p>
            </div>
          )}
        </div>
        )}

        {/* Portrait + feedback panel + connector overlay */}
        <div className="relative flex flex-wrap items-start gap-7 flex-1 min-w-0">
          <div className="relative w-[440px] max-w-full h-[587px] shrink-0 rounded-card overflow-hidden border border-hairline-soft shadow-[0_24px_48px_-12px_rgba(0,0,0,0.8)] bg-vein">
            {focusDetail && focusDetail.processed_image_url ? (
              <img
                src={focusDetail.processed_image_url}
                alt="Focus scan portrait"
                className="w-full h-full object-cover"
                onError={handleImageError}
              />
            ) : (
              <div className="w-full h-full bg-vein" role="img" aria-label="focus scan placeholder" />
            )}
            {focusScore100 != null && (
              <PortraitScoreBar
                score={formatScoreOutOfTen(focusScore100)}
                size="xl"
                inset="6%"
                lifted={lifted}
                title="Press and hold to reveal your face"
                className="cursor-grab"
                {...liftHandlers}
              />
            )}
          </div>

          {isTax ? (
            // One connected widget: FACE BREAKDOWN (master) + SCAN DETAIL (detail),
            // same height as the portrait, split by a divider rather than a gap.
            <div className="relative z-[4] flex h-[587px] flex-1 min-w-[560px] bg-[rgba(32,32,31,0.72)] border border-hairline-soft rounded-panel overflow-hidden">
              <div className="w-[296px] shrink-0 border-r border-hairline-soft">
                <CategoryBreakdown
                  bare
                  categories={categoryScores}
                  selectedKey={activeCat}
                  onSelect={setSelectedCat}
                />
              </div>
              <div className="flex-1 min-w-0 overflow-y-auto scrollbar-hide">
                <CategoryDetail
                  flush
                  categoryKey={activeCat}
                  score={catScoreMap[activeCat]}
                  summary={catSummaryMap[activeCat]}
                  subScores={subScores}
                  subNotes={subNotes}
                />
              </div>
            </div>
          ) : (
            <FacialFeedbackPanel vectors={vectors} />
          )}
        </div>
      </motion.div>

      {/* ── DESKTOP structure (geometry) ──────────────────────────────── */}
      <GeometryPanel geometry={geometry} className="hidden lg:block mt-10 border-t border-hairline-soft pt-7" />

      {/* ── DESKTOP regimen ───────────────────────────────────────────── */}
      {products.length > 0 && (
        <div className="hidden lg:block mt-10 border-t border-hairline-soft pt-7">
          <div className="flex justify-between items-end">
            <div>
              <div className="flex items-center gap-3">
                <LuFlaskConical size={16} className="text-aurum" aria-hidden="true" />
                <span
                  className="font-display uppercase tracking-[0.24em] text-[#E8E2D6]"
                  style={{ fontSize: 12 }}
                >
                  {COPY.VAULT_REGIMEN_HEADER}
                </span>
              </div>
              <p className="font-display italic text-bone-dim m-0 mt-2" style={{ fontSize: 18 }}>
                Curated from this scan&rsquo;s results. Updated as you progress.
              </p>
            </div>
            <div className="flex items-center gap-5">
              <span className="font-display text-bone-dim" style={{ fontSize: 15 }}>
                {products.length} {products.length === 1 ? 'step' : 'steps'}
                {regimenTotal(products) && (
                  <>
                    {' '}&middot; <span className="text-aurum">{regimenTotal(products)}</span>
                  </>
                )}
              </span>
              <InertChrome label="VIEW FULL PLAN" icon={<LuArrowRight size={15} />} />
            </div>
          </div>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-[18px] mt-5">
            {products.map((p, i) => (
              <RegimenGridCard key={`${p.brand}-${p.product_name}-${i}`} product={p} />
            ))}
          </div>
        </div>
      )}

      {/* ── DESKTOP routine reference (taxonomy scans — products live in /routine) ── */}
      {isTax && (
        <RoutineRefBlock className="hidden lg:flex mt-10 border-t border-hairline-soft pt-7" />
      )}

      {/* ── MOBILE ───────────────────────────────────────────────────── */}
      <div className="lg:hidden flex flex-col">
        {/* Legacy scans stack the score above; taxonomy overlays it on the portrait. */}
        {!isTax && <div className="mt-3.5">{mobileScoreBlock}</div>}

        {/* Metrics over portrait */}
        <div className="relative mt-3.5 h-[442px] overflow-hidden -mr-5">
          {/* Score overlaid on the portrait's faded-left region (taxonomy scans). */}
          {isTax && (
            <div className="absolute left-0 top-0 z-10 max-w-[60%]">
              {mobileScoreBlock}
            </div>
          )}
          {focusDetail && focusDetail.processed_image_url ? (
            <img
              src={focusDetail.processed_image_url}
              alt="Scan portrait"
              className="absolute right-[-44px] top-0 w-[330px] h-[442px] object-cover"
              style={{
                WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 38%)',
                maskImage: 'linear-gradient(90deg, transparent, #000 38%)',
              }}
              onError={handleImageError}
            />
          ) : (
            <div
              className="absolute right-[-44px] top-0 w-[330px] h-[442px] bg-vein"
              style={{
                WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 38%)',
                maskImage: 'linear-gradient(90deg, transparent, #000 38%)',
              }}
              role="img"
              aria-label="scan portrait placeholder"
            />
          )}
          {/* Legacy vector overlay (taxonomy uses the selectable breakdown below). */}
          {!isTax && (
            <div className="absolute left-0 top-2 w-[196px]">
              {vectors.map((v) => (
                <div
                  key={v.name}
                  className="flex justify-between items-baseline py-[9.5px] border-b border-[rgba(201,164,92,0.30)]"
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="text-bone-dim inline-flex" aria-hidden="true">
                      {vectorGlyph(v.name)}
                    </span>
                    <span className="font-display text-[#D8D2C6]" style={{ fontSize: 14 }}>
                      {VECTOR_DISPLAY_LABELS[v.name] || v.name}
                    </span>
                  </span>
                  <span className="font-display font-medium text-aurum tabular-nums" style={{ fontSize: 17 }}>
                    {v.score}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selectable category breakdown → opens the detail modal (taxonomy scans) */}
        {isTax && (
          <div className="mt-4">
            <CategoryBreakdown
              categories={categoryScores}
              selectedKey={catModalOpen ? selectedCat : null}
              onSelect={openMobileCat}
            />
          </div>
        )}

        {/* Structure (geometry) */}
        <GeometryPanel geometry={geometry} className="mt-4" />

        {/* Regimen */}
        {products.length > 0 && (
          <div className="mt-4 bg-[#151514] border border-hairline-soft rounded-card p-4">
            <div className="flex gap-3">
              <LuFlaskConical size={19} className="text-[#B8B2A6] mt-0.5 shrink-0" aria-hidden="true" />
              <div>
                <p
                  className="font-display uppercase tracking-[0.18em] text-[#E8E2D6] m-0"
                  style={{ fontSize: 11.5 }}
                >
                  {COPY.VAULT_REGIMEN_HEADER}
                </p>
                <p className="font-display italic text-bone-dim m-0 mt-0.5" style={{ fontSize: 13.5 }}>
                  {COPY.VAULT_REGIMEN_SUBTITLE}
                </p>
              </div>
            </div>
            {products.map((p, i) => (
              <MobileRegimenRow key={`${p.brand}-${p.product_name}-${i}`} product={p} />
            ))}
            <div
              className="mt-3.5 text-center py-3 border border-[rgba(255,255,255,0.10)] rounded-thumb font-display uppercase tracking-[0.22em] text-[#D8D2C6]"
              style={{ fontSize: 11.5 }}
              aria-disabled="true"
            >
              {COPY.VAULT_VIEW_FULL_REGIMEN_CTA} &nbsp;&rsaquo;
            </div>
          </div>
        )}

        {/* Mobile routine reference (taxonomy scans) */}
        {isTax && <RoutineRefBlock mobile className="mt-4" />}
      </div>

      {/* Mobile category-detail modal */}
      {isTax && (
        <CategoryDetailModal
          open={catModalOpen}
          onClose={() => setCatModalOpen(false)}
          categoryKey={selectedCat}
          score={catScoreMap[selectedCat]}
          summary={catSummaryMap[selectedCat]}
          subScores={subScores}
          subNotes={subNotes}
        />
      )}
    </div>
  );
};

// Routine reference — products now live on the /routine page; the Vault links out.
const RoutineRefBlock = ({ className = '', mobile = false }) => (
  <div
    className={[
      mobile
        ? 'bg-[#151514] border border-hairline-soft rounded-card p-4 flex items-center justify-between gap-3'
        : 'bg-[#161615] border border-hairline-soft rounded-card p-6 items-center justify-between gap-4',
      className,
    ].join(' ')}
  >
    <div className="flex items-start gap-3 min-w-0">
      <LuClipboardList size={mobile ? 19 : 20} className="text-aurum shrink-0 mt-0.5" aria-hidden="true" />
      <div className="min-w-0">
        <p className="font-display uppercase tracking-[0.2em] text-[#E8E2D6] m-0" style={{ fontSize: mobile ? 11.5 : 12 }}>
          {COPY.VERDICT_ROUTINE_REF_HEADLINE}
        </p>
        <p className="font-display italic text-bone-dim m-0 mt-1" style={{ fontSize: mobile ? 13.5 : 15 }}>
          {COPY.VERDICT_ROUTINE_REF_SUBTITLE}
        </p>
      </div>
    </div>
    <Link
      to={ROUTES.ROUTINE}
      className="inline-flex items-center gap-2 shrink-0 no-underline rounded-thumb border border-[rgba(201,164,92,0.45)] px-4 py-2.5 font-display uppercase tracking-[0.2em] text-aurum-bright hover:border-aurum-bright transition-colors duration-200"
      style={{ fontSize: 11 }}
    >
      <span>{COPY.VERDICT_OPEN_ROUTINE_CTA}</span>
      <LuArrowRight size={14} />
    </Link>
  </div>
);

// ── Sub-components ─────────────────────────────────────────────────────────

// STRUCTURE — deterministic 2D geometry read (pipeline Stage 2). A diagnostic
// "strong / refine" overlay, supplementary to the Gemini facial feedback.
// Renders nothing when the scan carries no geometry (pre-deploy or no face).
const GeometryPanel = ({ geometry, className = '' }) => {
  if (!geometry || !Array.isArray(geometry.metrics) || geometry.metrics.length === 0) {
    return null;
  }
  return (
    <section aria-label={COPY.VERDICT_GEOMETRY_HEADLINE} className={className}>
      <div className="flex items-center gap-3">
        <LuSlidersHorizontal size={16} className="text-aurum" aria-hidden="true" />
        <span
          className="font-display uppercase tracking-[0.24em] text-[#E8E2D6]"
          style={{ fontSize: 12 }}
        >
          {COPY.VERDICT_GEOMETRY_HEADLINE}
        </span>
      </div>
      <p className="font-display italic text-bone-dim m-0 mt-2" style={{ fontSize: 15 }}>
        {COPY.VERDICT_GEOMETRY_SUBTITLE}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-9 mt-4">
        {geometry.metrics.map((m) => {
          const strong = m.band === 'strong';
          return (
            <div
              key={m.key}
              className="flex items-center justify-between gap-4 py-3 border-b border-[rgba(255,255,255,0.06)]"
            >
              <span className="font-display uppercase tracking-[0.16em] text-[#D8D2C6]" style={{ fontSize: 13 }}>
                {m.label}
              </span>
              <span className="inline-flex items-baseline gap-2.5 shrink-0">
                <span className="font-display text-bone-dim tabular-nums" style={{ fontSize: 17 }}>
                  {m.score}
                </span>
                <span
                  className={[
                    'font-display uppercase tracking-[0.14em]',
                    strong ? 'text-aurum' : 'text-bone-dim',
                  ].join(' ')}
                  style={{ fontSize: 10.5 }}
                >
                  {strong ? COPY.GEOMETRY_BAND_STRONG : COPY.GEOMETRY_BAND_ADDRESSABLE}
                </span>
              </span>
            </div>
          );
        })}
      </div>
      <p className="font-display italic text-bone-dim m-0 mt-3" style={{ fontSize: 13 }}>
        {COPY.VERDICT_GEOMETRY_NOTE}
      </p>
    </section>
  );
};

const FacialFeedbackPanel = ({ vectors }) => (
  <div className="w-[296px] max-w-full bg-[rgba(32,32,31,0.72)] border border-hairline-soft rounded-panel p-5 z-[4] relative">
    <h2
      className="font-display uppercase tracking-[0.24em] text-[#E8E2D6] m-0 h-[34px] border-b border-hairline-soft"
      style={{ fontSize: 12 }}
    >
      {COPY.VAULT_FACIAL_FEEDBACK_HEADER}
    </h2>
    {(!vectors || vectors.length === 0) ? (
      <p className="font-display italic text-bone-dim text-center py-8 m-0" style={{ fontSize: 13 }}>
        Awaiting analysis.
      </p>
    ) : (
      vectors.map((v, i) => (
        <div
          key={v.name}
          className={[
            'flex items-center gap-[13px] h-[56px]',
            i < vectors.length - 1 ? 'border-b border-[rgba(255,255,255,0.06)]' : '',
          ].join(' ')}
        >
          <span className="text-bone-dim shrink-0 inline-flex" aria-hidden="true">
            {vectorGlyph(v.name)}
          </span>
          <div>
            <p
              className="font-display uppercase tracking-[0.16em] text-[#E8E2D6] m-0"
              style={{ fontSize: 12 }}
            >
              {VECTOR_DISPLAY_LABELS[v.name] || v.name}
            </p>
            <p className="font-display text-bone-dim m-0 mt-px" style={{ fontSize: 15 }}>
              {vectorDescriptor(v.score)}
            </p>
          </div>
        </div>
      ))
    )}
  </div>
);

const RegimenGridCard = ({ product }) => (
  <div className="bg-[#161615] border border-hairline-soft rounded-panel p-[18px] flex flex-col">
    <div className="flex justify-between items-start">
      <div className="w-[62px] h-[80px] rounded-lg bg-gradient-to-b from-[#3b3b39] to-[#232322] border border-hairline-soft flex items-center justify-center shrink-0 overflow-hidden">
        {product.image_url ? (
          <img src={product.image_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <LuFlaskConical size={22} className="text-bone-dim" aria-hidden="true" />
        )}
      </div>
      <span
        className="font-display uppercase tracking-[0.2em] text-aurum border border-[rgba(201,164,92,0.4)] rounded-full px-[11px] py-[5px]"
        style={{ fontSize: 10 }}
      >
        {VECTOR_DISPLAY_LABELS[product.vector] || product.vector}
      </span>
    </div>
    <p className="font-display text-bone m-0 mt-4" style={{ fontSize: 18, lineHeight: 1.25 }}>
      {product.product_name}
    </p>
    <p className="font-display text-bone-dim m-0 mt-0.5" style={{ fontSize: 14 }}>
      {product.brand}
    </p>
    {product.description && (
      <p className="font-display text-[#B8B2A6] m-0 mt-[11px]" style={{ fontSize: 14, lineHeight: 1.45 }}>
        <span className="uppercase tracking-[0.16em] text-aurum" style={{ fontSize: 10 }}>
          WHY &middot;{' '}
        </span>
        <span className="italic">{product.description}</span>
      </p>
    )}
    <div className="mt-auto pt-[18px] flex justify-between items-center">
      {product.price_range ? (
        <span className="font-display font-medium text-bone" style={{ fontSize: 21 }}>
          {product.price_range}
        </span>
      ) : (
        <span aria-hidden="true" />
      )}
      <a
        href={product.affiliate_url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={() => trackAffiliate(product, product.vector)}
        aria-label={`Shop ${product.brand} ${product.product_name}`}
        className="flex items-center gap-2 bg-transparent border border-[rgba(255,255,255,0.18)] rounded-thumb px-[15px] py-[9px] cursor-pointer text-[#E8E2D6] no-underline font-display uppercase tracking-[0.2em] transition-colors duration-200 hover:border-aurum hover:text-aurum-bright"
        style={{ fontSize: 11 }}
      >
        <span>SHOP</span>
        <LuExternalLink size={13} aria-hidden="true" />
      </a>
    </div>
  </div>
);

const MobileRegimenRow = ({ product }) => (
  <a
    href={product.affiliate_url}
    target="_blank"
    rel="noopener noreferrer sponsored"
    onClick={() => trackAffiliate(product, product.vector)}
    aria-label={`${product.brand} ${product.product_name}`}
    className="flex gap-[13px] bg-[#1D1D1C] border border-[rgba(255,255,255,0.06)] rounded-field p-3.5 mt-3 no-underline"
  >
    <div className="w-[54px] h-[72px] rounded-lg bg-gradient-to-b from-[#3b3b39] to-[#232322] border border-hairline-soft flex items-center justify-center shrink-0 overflow-hidden">
      {product.image_url ? (
        <img src={product.image_url} alt="" className="w-full h-full object-cover" />
      ) : (
        <LuFlaskConical size={20} className="text-bone-dim" aria-hidden="true" />
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p
        className="font-display uppercase tracking-[0.22em] text-[#E8E2D6] m-0"
        style={{ fontSize: 10.5 }}
      >
        {VECTOR_DISPLAY_LABELS[product.vector] || product.vector}
      </p>
      <p className="font-display text-bone m-0 mt-1" style={{ fontSize: 16 }}>
        {product.brand}
      </p>
      <p className="font-display text-bone m-0" style={{ fontSize: 16 }}>
        {product.product_name}
      </p>
      {product.price_range && (
        <p className="font-display text-bone-dim m-0 mt-0.5" style={{ fontSize: 14 }}>
          {product.price_range}
        </p>
      )}
      {product.description && (
        <p className="m-0 mt-1.5" style={{ fontSize: 13 }}>
          <span className="font-display uppercase tracking-[0.16em] text-aurum" style={{ fontSize: 10 }}>
            WHY:
          </span>{' '}
          <span className="font-display italic text-bone-dim">{product.description}</span>
        </p>
      )}
    </div>
    <span
      className="w-7 h-7 border border-[rgba(255,255,255,0.12)] rounded-[7px] flex items-center justify-center shrink-0 text-bone-dim"
      aria-hidden="true"
    >
      <LuExternalLink size={13} />
    </span>
  </a>
);

// FILTER / EXPORT / VIEW FULL PLAN — design chrome with no backend yet.
const InertChrome = ({ label, icon }) => (
  <button
    type="button"
    aria-disabled="true"
    className="flex items-center gap-2 bg-transparent border-0 cursor-default text-[#D8D2C6] font-display uppercase tracking-[0.22em] p-0"
    style={{ fontSize: 12 }}
  >
    <span>{label}</span>
    {icon && <span className="inline-flex" aria-hidden="true">{icon}</span>}
  </button>
);

export default VaultFocusView;
